import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  ThumbUp as ThumbUpIcon,
  Block as BlockIcon,
  ContentCopy as DuplicateIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from "@mui/icons-material";
import JoditEditor from "jodit-react";
import { toast } from "react-toastify";
import {
  getJob,
  getDraftQuestions,
  updateDraftQuestion,
  setReviewStatus,
  setUseExisting,
  approveJob,
  bulkClassifyDrafts,
  deleteDraftQuestion,
  getSubjects,
  getTopicsBySubject,
  getSubtopicsByTopic,
  createTopic,
  createSubtopic,
} from "./test-upload.service";

// Sentinel value used as a dropdown option to mean "open the Create dialog".
// Chosen to be unambiguous (no real id will ever look like this string).
const CREATE_OPTION = "__CREATE_NEW__";

const REVIEW_COLOR = {
  PENDING: "#f59e0b",
  EDITED: "#3366FF",
  APPROVED: "#22c55e",
  REJECTED: "#dc3545",
  FLAGGED: "#dc3545",
};

const FLAG_LABEL = {
  DUPLICATE: "Duplicate of existing question",
  NO_CORRECT_ANSWER: "No correct answer marked",
  TOO_FEW_OPTIONS: "Fewer than 2 options",
  MULTIPLE_CORRECT_FOR_SINGLE: "Multiple correct on a single-choice question",
  SUBJECT_UNRESOLVED: "Subject could not be detected",
  LOW_SUBJECT_CONFIDENCE: "Low-confidence subject",
};

// Static config keeps Jodit's internal toolbar/handler refs stable across renders.
// Recreating this object on every render would force a full editor re-init and steal focus.
const JODIT_CONFIG = {
  readonly: false,
  toolbarAdaptive: false,
  toolbarSticky: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  enableDragAndDropFileToEditor: true,
  uploader: { insertImageAsBase64URI: true },
  height: 220,
  buttons:
    "bold,italic,underline,|,ul,ol,|,outdent,indent,|,font,fontsize,brush,|,image,link,|,align,|,undo,redo,|,source",
};

// Trimmed-down editor for options — options are typically short so a compact toolbar +
// smaller height keeps the UI dense while still allowing formatting + inline images.
const JODIT_OPTION_CONFIG = {
  readonly: false,
  toolbarAdaptive: false,
  toolbarSticky: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  enableDragAndDropFileToEditor: true,
  uploader: { insertImageAsBase64URI: true },
  height: 110,
  buttons: "bold,italic,underline,|,image,|,source",
};

const parseFlags = (raw) => {
  if (!raw) return [];
  try {
    return JSON.parse(raw);
  } catch (e) {
    return [];
  }
};

export default function TestUploadReview() {
  const { jobId } = useParams();
  const history = useHistory();
  const [job, setJob] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [approvingJob, setApprovingJob] = useState(false);
  // Delete-draft confirmation. Holds the target question (or null when closed) so the dialog can
  // show its number/preview; deletingId drives the in-flight spinner on the confirm button.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Which cards are in edit mode. Cards render read-only (rendered HTML) by default and only
  // mount the heavy Jodit editors when expanded — a 50-question job would otherwise spin up
  // 300+ editor instances at once. Default: all collapsed.
  const [editingIds, setEditingIds] = useState(() => new Set());
  // Snapshot of each question captured when it enters edit mode, so Cancel can revert. Local edits
  // are immutable (updateLocal/updateAnswerLocal replace objects rather than mutating), so holding
  // the pre-edit object reference is a valid snapshot of question + answers + classification.
  const editSnapshots = useRef({});
  // Live mirror of editingIds for the blur handlers — lets them ignore a stray blur fired while/after
  // a card is collapsed (e.g. on editor unmount during Cancel), which would otherwise clobber a revert.
  const editingIdsRef = useRef(editingIds);
  editingIdsRef.current = editingIds;
  const isEditing = (id) => editingIds.has(id);
  const enterEdit = (id) => {
    const snapshot = questions.find((q) => q.draftQuestionId === id);
    if (snapshot) editSnapshots.current[id] = snapshot;
    setEditingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };
  // Collapse after a successful Save — the saved state becomes the new baseline, snapshot discarded.
  const exitEdit = (id) => {
    delete editSnapshots.current[id];
    setEditingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };
  // Discard in-progress edits: restore the snapshot taken at enterEdit, then collapse.
  const cancelEdit = (id) => {
    const snapshot = editSnapshots.current[id];
    if (snapshot) {
      setQuestions((prev) => prev.map((q) => (q.draftQuestionId === id ? snapshot : q)));
    }
    exitEdit(id);
  };

  // Bulk-select mode + bulk classification dropdowns
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkSubjectId, setBulkSubjectId] = useState("");
  const [bulkTopicId, setBulkTopicId] = useState("");
  const [bulkSubtopicId, setBulkSubtopicId] = useState("");
  const [bulkTopics, setBulkTopics] = useState([]);
  const [bulkSubtopics, setBulkSubtopics] = useState([]);
  const [bulkApplying, setBulkApplying] = useState(false);

  // Inline "+ Create new topic/subtopic" dialog. Shared across bulk + per-row dropdowns so the
  // open() callback determines what to do with the new entry (auto-select in the right spot).
  // Shape: { open, mode: 'topic'|'subtopic', parentId, parentLabel, onCreated }
  const [taxonomyDialog, setTaxonomyDialog] = useState({ open: false });
  const [taxonomyDialogValue, setTaxonomyDialogValue] = useState("");
  const [taxonomyDialogSubmitting, setTaxonomyDialogSubmitting] = useState(false);

  // Per-question taxonomy cache: { [subjectId]: [topics], [topicId]: [subtopics] }
  // Keeps dropdowns responsive without re-fetching on every render.
  const topicsCache = useRef({});
  const subtopicsCache = useRef({});
  const [, forceTaxonomyRender] = useState(0);
  const bumpTaxonomy = () => forceTaxonomyRender((n) => n + 1);

  const load = useCallback(async () => {
    setLoading(true);
    // Fresh server truth — collapse any open editors and drop their snapshots.
    setEditingIds(new Set());
    editSnapshots.current = {};
    try {
      const [jobRes, qRes] = await Promise.all([getJob(jobId), getDraftQuestions(jobId)]);
      setJob(jobRes?.result || null);
      setQuestions(qRes?.result || []);
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Failed to load review");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    load();
    getSubjects()
      .then((res) => setSubjects(res?.result || []))
      .catch(() => setSubjects([]));
  }, [load]);

  // Pre-warm taxonomy caches for every distinct subject/topic the loaded questions reference,
  // so per-row dropdowns show options immediately on first open.
  useEffect(() => {
    const subjectIds = Array.from(
      new Set(questions.map((q) => q.subjectId).filter((id) => id))
    );
    const topicIds = Array.from(
      new Set(questions.map((q) => q.topicId).filter((id) => id))
    );
    subjectIds.forEach((sid) => {
      if (!topicsCache.current[sid]) {
        getTopicsBySubject(sid)
          .then((res) => {
            topicsCache.current[sid] = res?.topics || [];
            bumpTaxonomy();
          })
          .catch(() => {
            topicsCache.current[sid] = [];
          });
      }
    });
    topicIds.forEach((tid) => {
      if (!subtopicsCache.current[tid]) {
        getSubtopicsByTopic(tid)
          .then((res) => {
            subtopicsCache.current[tid] = res?.subtopics || [];
            bumpTaxonomy();
          })
          .catch(() => {
            subtopicsCache.current[tid] = [];
          });
      }
    });
  }, [questions]);

  // Bulk subject change -> reset topic + subtopic and load topics for that subject.
  useEffect(() => {
    setBulkTopicId("");
    setBulkSubtopicId("");
    setBulkTopics([]);
    setBulkSubtopics([]);
    if (!bulkSubjectId) return;
    if (topicsCache.current[bulkSubjectId]) {
      setBulkTopics(topicsCache.current[bulkSubjectId]);
      return;
    }
    getTopicsBySubject(bulkSubjectId)
      .then((res) => {
        const t = res?.topics || [];
        topicsCache.current[bulkSubjectId] = t;
        setBulkTopics(t);
      })
      .catch(() => setBulkTopics([]));
  }, [bulkSubjectId]);

  // Bulk topic change -> reset subtopic and load subtopics for that topic.
  useEffect(() => {
    setBulkSubtopicId("");
    setBulkSubtopics([]);
    if (!bulkTopicId) return;
    if (subtopicsCache.current[bulkTopicId]) {
      setBulkSubtopics(subtopicsCache.current[bulkTopicId]);
      return;
    }
    getSubtopicsByTopic(bulkTopicId)
      .then((res) => {
        const st = res?.subtopics || [];
        subtopicsCache.current[bulkTopicId] = st;
        setBulkSubtopics(st);
      })
      .catch(() => setBulkSubtopics([]));
  }, [bulkTopicId]);

  const updateLocal = (draftQuestionId, patch) => {
    setQuestions((prev) =>
      prev.map((q) => (q.draftQuestionId === draftQuestionId ? { ...q, ...patch } : q))
    );
  };

  const updateAnswerLocal = (draftQuestionId, idx, patch) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.draftQuestionId !== draftQuestionId) return q;
        const answers = (q.answers || []).map((a, i) => (i === idx ? { ...a, ...patch } : a));
        return { ...q, answers };
      })
    );
  };

  // Cascading per-question subject change: clears topic + subtopic (no longer valid) and primes
  // the cache for the new subject.
  const handleSubjectChange = (q, newSubjectId) => {
    updateLocal(q.draftQuestionId, {
      subjectId: newSubjectId,
      subjectName: subjects.find((s) => s.subjectId === newSubjectId)?.subject || "",
      topicId: null,
      topicName: "",
      subtopicId: null,
      subtopicName: "",
    });
    if (newSubjectId && !topicsCache.current[newSubjectId]) {
      getTopicsBySubject(newSubjectId)
        .then((res) => {
          topicsCache.current[newSubjectId] = res?.topics || [];
          bumpTaxonomy();
        })
        .catch(() => {
          topicsCache.current[newSubjectId] = [];
        });
    }
  };

  const handleTopicChange = (q, newTopicId) => {
    const newTopicName =
      (topicsCache.current[q.subjectId] || []).find((t) => t.id === newTopicId)?.name || "";
    updateLocal(q.draftQuestionId, {
      topicId: newTopicId,
      topicName: newTopicName,
      subtopicId: null,
      subtopicName: "",
    });
    if (newTopicId && !subtopicsCache.current[newTopicId]) {
      getSubtopicsByTopic(newTopicId)
        .then((res) => {
          subtopicsCache.current[newTopicId] = res?.subtopics || [];
          bumpTaxonomy();
        })
        .catch(() => {
          subtopicsCache.current[newTopicId] = [];
        });
    }
  };

  const handleSubtopicChange = (q, newSubtopicId) => {
    const newSubtopicName =
      (subtopicsCache.current[q.topicId] || []).find((st) => st.id === newSubtopicId)?.name || "";
    updateLocal(q.draftQuestionId, {
      subtopicId: newSubtopicId,
      subtopicName: newSubtopicName,
    });
  };

  const openCreateDialog = ({ mode, parentId, parentLabel, onCreated }) => {
    setTaxonomyDialog({ open: true, mode, parentId, parentLabel, onCreated });
    setTaxonomyDialogValue("");
  };

  const closeCreateDialog = () => {
    setTaxonomyDialog({ open: false });
    setTaxonomyDialogValue("");
  };

  const submitCreateDialog = async () => {
    const name = taxonomyDialogValue.trim();
    if (!name) {
      toast.warn("Please enter a name");
      return;
    }
    setTaxonomyDialogSubmitting(true);
    try {
      const { mode, parentId, onCreated } = taxonomyDialog;
      const res =
        mode === "topic"
          ? await createTopic(parentId, name)
          : await createSubtopic(parentId, name);
      const created = res?.result;
      if (!created?.id) {
        throw new Error(res?.msg || "Failed to create");
      }
      // Append to the right cache so the new entry is selectable everywhere immediately.
      if (mode === "topic") {
        const list = topicsCache.current[parentId] || [];
        if (!list.some((t) => t.id === created.id)) {
          topicsCache.current[parentId] = [...list, { id: created.id, name: created.name }];
        }
      } else {
        const list = subtopicsCache.current[parentId] || [];
        if (!list.some((st) => st.id === created.id)) {
          subtopicsCache.current[parentId] = [...list, { id: created.id, name: created.name }];
        }
      }
      bumpTaxonomy();
      if (onCreated) onCreated(created);
      toast.success(created.created === false ? "Existing entry selected" : `${mode === "topic" ? "Topic" : "Sub-topic"} created`);
      closeCreateDialog();
    } catch (e) {
      toast.error(typeof e === "string" ? e : e?.message || "Create failed");
    } finally {
      setTaxonomyDialogSubmitting(false);
    }
  };

  const handleSave = async (q) => {
    setSavingId(q.draftQuestionId);
    try {
      const correctCount = (q.answers || []).filter((a) => a.correct).length;
      const payload = {
        ...q,
        optionType: correctCount > 1 ? "Multi" : "Single",
        reviewStatus: "EDITED",
      };
      await updateDraftQuestion(q.draftQuestionId, payload);
      updateLocal(q.draftQuestionId, { reviewStatus: "EDITED", optionType: payload.optionType });
      exitEdit(q.draftQuestionId);
      toast.success("Saved");
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const handleReviewStatus = async (q, status) => {
    try {
      await setReviewStatus(q.draftQuestionId, status);
      updateLocal(q.draftQuestionId, { reviewStatus: status });
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Update failed");
    }
  };

  // Reuse decision for a detected duplicate. useExisting=true (default) reuses the matched live
  // question on publish; false forces publish to insert this draft as a brand-new question. Persisted
  // immediately via the dedicated endpoint (handleSave's updateDraftQuestion does NOT touch this flag).
  const handleUseExisting = async (q, useExisting) => {
    try {
      await setUseExisting(q.draftQuestionId, useExisting);
      updateLocal(q.draftQuestionId, { useExistingQuestion: useExisting });
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Update failed");
    }
  };

  // Permanently remove a draft question (backend cascades its answers). On success we drop it from
  // local state and clean up any edit snapshot / selection so no stale references survive.
  const handleDelete = async (draftQuestionId) => {
    setDeletingId(draftQuestionId);
    try {
      await deleteDraftQuestion(draftQuestionId);
      setQuestions((prev) => prev.filter((q) => q.draftQuestionId !== draftQuestionId));
      delete editSnapshots.current[draftQuestionId];
      setEditingIds((prev) => {
        const next = new Set(prev);
        next.delete(draftQuestionId);
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(draftQuestionId);
        return next;
      });
      setDeleteTarget(null);
      toast.success("Question deleted");
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const handleApproveJob = async () => {
    setApprovingJob(true);
    try {
      await approveJob(jobId);
      toast.success("Job approved. It is now available for the admin to publish.");
      load();
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Approve failed");
    } finally {
      setApprovingJob(false);
    }
  };

  const toggleSelect = (draftQuestionId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(draftQuestionId)) next.delete(draftQuestionId);
      else next.add(draftQuestionId);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(questions.map((q) => q.draftQuestionId)));
  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkApply = async () => {
    if (selectedIds.size === 0) {
      toast.warn("Select at least one question");
      return;
    }
    if (!bulkSubjectId && !bulkTopicId && !bulkSubtopicId) {
      toast.warn("Pick at least one of Subject / Topic / Subtopic to apply");
      return;
    }
    setBulkApplying(true);
    try {
      const res = await bulkClassifyDrafts(jobId, {
        draftQuestionIds: Array.from(selectedIds),
        subjectId: bulkSubjectId || null,
        topicId: bulkTopicId || null,
        subtopicId: bulkSubtopicId || null,
      });
      toast.success(res?.msg || `${res?.result || 0} question(s) updated`);
      // Re-fetch from the server (the source of truth) rather than optimistically patching local
      // state. The bulk SQL cascade-clears child levels — e.g. changing only the subject NULLs the
      // existing topic/subtopic — and replicating that CASE logic on the client would drift from the
      // backend and could leave a stale topic that a later per-question Save would persist.
      clearSelection();
      setBulkSubjectId("");
      await load();
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Bulk update failed");
    } finally {
      setBulkApplying(false);
    }
  };

  // Stable per-row Jodit onBlur handlers — avoids re-creating handlers each render which causes
  // the editor to remount and steal focus mid-typing. (questions length is the only signal we need
  // to refresh closures after load.)
  const questionEditors = useMemo(() => {
    const map = {};
    questions.forEach((q) => {
      map[q.draftQuestionId] = {
        onQuestionBlur: (newContent) => {
          if (!editingIdsRef.current.has(q.draftQuestionId)) return;
          const html = typeof newContent === "string" ? newContent : newContent?.target?.innerHTML;
          if (html != null) updateLocal(q.draftQuestionId, { question: html });
        },
        onExplanationBlur: (newContent) => {
          if (!editingIdsRef.current.has(q.draftQuestionId)) return;
          const html = typeof newContent === "string" ? newContent : newContent?.target?.innerHTML;
          if (html != null) updateLocal(q.draftQuestionId, { answerExplanation: html });
        },
        onReferenceBlur: (newContent) => {
          if (!editingIdsRef.current.has(q.draftQuestionId)) return;
          const html = typeof newContent === "string" ? newContent : newContent?.target?.innerHTML;
          if (html != null) updateLocal(q.draftQuestionId, { reference: html });
        },
      };
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions.length]);

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <IconButton onClick={() => history.push("/app/test-upload")}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {job?.jobName || "Review"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {questions.length} questions · status {job?.status}
          </Typography>
        </Box>
        <Button
          variant={selectMode ? "contained" : "outlined"}
          color="primary"
          onClick={() => {
            setSelectMode((m) => !m);
            if (selectMode) clearSelection();
          }}
        >
          {selectMode ? "Exit select" : "Select multiple"}
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<ThumbUpIcon />}
          disabled={approvingJob || job?.status === "PUBLISHED"}
          onClick={handleApproveJob}
        >
          {approvingJob ? <CircularProgress size={20} color="inherit" /> : "Approve Job"}
        </Button>
      </Box>

      {job?.status === "APPROVED" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          This job is approved. An admin can now publish it as a test from the admin portal.
        </Alert>
      )}

      {/* Bulk action bar */}
      {selectMode && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#f8fafc",
            borderColor: "#3366FF",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {selectedIds.size} selected
            </Typography>
            <Button size="small" onClick={selectAll}>
              Select all
            </Button>
            <Button size="small" onClick={clearSelection} disabled={selectedIds.size === 0}>
              Clear
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                label="Subject"
                value={bulkSubjectId}
                onChange={(e) => setBulkSubjectId(e.target.value)}
              >
                <MenuItem value="">
                  <em>— no change —</em>
                </MenuItem>
                {subjects.map((s) => (
                  <MenuItem key={s.subjectId} value={s.subjectId}>
                    {s.subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={!bulkSubjectId}>
              <InputLabel>Topic</InputLabel>
              <Select
                label="Topic"
                value={bulkTopicId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === CREATE_OPTION) {
                    openCreateDialog({
                      mode: "topic",
                      parentId: bulkSubjectId,
                      parentLabel: subjects.find((s) => s.subjectId === bulkSubjectId)?.subject || "",
                      onCreated: (created) => setBulkTopicId(created.id),
                    });
                    return;
                  }
                  setBulkTopicId(v);
                }}
              >
                <MenuItem value="">
                  <em>— no change —</em>
                </MenuItem>
                {bulkTopics.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
                {bulkSubjectId && <Divider />}
                {bulkSubjectId && (
                  <MenuItem value={CREATE_OPTION} sx={{ color: "primary.main", fontWeight: 600 }}>
                    + Create new topic
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }} disabled={!bulkTopicId}>
              <InputLabel>Sub-topic</InputLabel>
              <Select
                label="Sub-topic"
                value={bulkSubtopicId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === CREATE_OPTION) {
                    openCreateDialog({
                      mode: "subtopic",
                      parentId: bulkTopicId,
                      parentLabel: bulkTopics.find((t) => t.id === bulkTopicId)?.name || "",
                      onCreated: (created) => setBulkSubtopicId(created.id),
                    });
                    return;
                  }
                  setBulkSubtopicId(v);
                }}
              >
                <MenuItem value="">
                  <em>— no change —</em>
                </MenuItem>
                {bulkSubtopics.map((st) => (
                  <MenuItem key={st.id} value={st.id}>
                    {st.name}
                  </MenuItem>
                ))}
                {bulkTopicId && <Divider />}
                {bulkTopicId && (
                  <MenuItem value={CREATE_OPTION} sx={{ color: "primary.main", fontWeight: 600 }}>
                    + Create new sub-topic
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="primary"
              onClick={handleBulkApply}
              disabled={bulkApplying || selectedIds.size === 0}
            >
              {bulkApplying ? <CircularProgress size={20} color="inherit" /> : "Apply to selected"}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Questions */}
      {questions.map((q, qi) => {
        const flags = parseFlags(q.errorFlags);
        const reviewColor = REVIEW_COLOR[q.reviewStatus] || "#666";
        const isSelected = selectedIds.has(q.draftQuestionId);
        const editors = questionEditors[q.draftQuestionId] || {};
        const rowTopics = topicsCache.current[q.subjectId] || [];
        const rowSubtopics = subtopicsCache.current[q.topicId] || [];
        return (
          <Paper
            key={q.draftQuestionId}
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              borderColor: isSelected ? "#3366FF" : undefined,
              borderWidth: isSelected ? 2 : 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
              {selectMode && (
                <Checkbox
                  checked={isSelected}
                  onChange={() => toggleSelect(q.draftQuestionId)}
                  sx={{ p: 0.5 }}
                />
              )}
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Q{qi + 1}
              </Typography>
              <Chip
                size="small"
                label={q.reviewStatus}
                sx={{ backgroundColor: reviewColor, color: "#fff" }}
              />
              {q.duplicateOfQuestionId > 0 && (
                <Tooltip
                  title={
                    q.useExistingQuestion === false
                      ? `Matches existing question #${q.duplicateOfQuestionId}, but will be published as a NEW question.`
                      : `Matches existing question #${q.duplicateOfQuestionId}. It will be reused on publish.`
                  }
                >
                  <Chip
                    size="small"
                    icon={<DuplicateIcon />}
                    label={`Duplicate of #${q.duplicateOfQuestionId}`}
                    color="warning"
                    variant="outlined"
                  />
                </Tooltip>
              )}
              {q.duplicateOfQuestionId > 0 && (
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={q.useExistingQuestion !== false}
                      onChange={(e) => handleUseExisting(q, e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      {q.useExistingQuestion !== false
                        ? `Reuse #${q.duplicateOfQuestionId}`
                        : "Insert as new"}
                    </Typography>
                  }
                />
              )}
              {q.subjectConfidence != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Subject ${(q.subjectConfidence * 100).toFixed(0)}%`}
                />
              )}
              {q.topicConfidence != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Topic ${(q.topicConfidence * 100).toFixed(0)}%`}
                />
              )}
              {q.subtopicConfidence != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Sub-topic ${(q.subtopicConfidence * 100).toFixed(0)}%`}
                />
              )}
              <Box sx={{ flexGrow: 1 }} />
              {/* Current classification, shown beside the flags so reviewers see the resolved
                  subject › topic › sub-topic at a glance without opening the card. */}
              {q.subjectName || q.topicName || q.subtopicName ? (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={[q.subjectName, q.topicName, q.subtopicName].filter(Boolean).join(" › ")}
                />
              ) : (
                <Chip size="small" variant="outlined" label="Unclassified" sx={{ color: "#94a3b8" }} />
              )}
              {flags.map((f) => (
                <Tooltip key={f} title={FLAG_LABEL[f] || f}>
                  <Chip size="small" color="error" variant="outlined" label={f} />
                </Tooltip>
              ))}
            </Box>

            {isEditing(q.draftQuestionId) ? (
            <>
            {/* ===== EDIT MODE: rich-text editors + classification ===== */}
            {/* Reused-duplicate footgun guard: dedup is stem+options only, so on reuse the live
                question #X is published as-is and any explanation/reference/difficulty edits made
                here are dropped. Editing the stem or an option changes the md5/tokens and auto-
                inserts fresh, so this only matters while reuse is still on. */}
            {q.duplicateOfQuestionId > 0 && q.useExistingQuestion !== false && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                This question will <strong>reuse existing #{q.duplicateOfQuestionId}</strong> on
                publish, so edits to the explanation, reference, or difficulty here won’t be
                published. Untick <strong>“Reuse #{q.duplicateOfQuestionId}”</strong> above (choose
                “Insert as new”) to publish your edited version instead.
              </Alert>
            )}
            {/* Rich-text editor for question */}
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Question
            </Typography>
            <Box sx={{ mb: 1 }}>
              <JoditEditor
                value={q.question || ""}
                config={JODIT_CONFIG}
                onBlur={editors.onQuestionBlur}
              />
            </Box>

            {/* Options — rich-text editor per option so faculty can format / paste images. */}
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Options
            </Typography>
            {(q.answers || []).map((a, ai) => (
              <Box key={ai} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!a.correct}
                      onChange={(e) =>
                        updateAnswerLocal(q.draftQuestionId, ai, { correct: e.target.checked })
                      }
                    />
                  }
                  label=""
                  sx={{ mr: 0, mt: 1 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <JoditEditor
                    value={a.answer || ""}
                    config={JODIT_OPTION_CONFIG}
                    onBlur={(newContent) => {
                      if (!editingIdsRef.current.has(q.draftQuestionId)) return;
                      const html =
                        typeof newContent === "string" ? newContent : newContent?.target?.innerHTML;
                      if (html != null) updateAnswerLocal(q.draftQuestionId, ai, { answer: html });
                    }}
                  />
                </Box>
              </Box>
            ))}

            {/* Rich-text editor for explanation */}
            <Typography variant="caption" sx={{ fontWeight: 600, mt: 1, display: "block" }}>
              Explanation
            </Typography>
            <Box sx={{ mb: 1 }}>
              <JoditEditor
                value={q.answerExplanation || ""}
                config={JODIT_CONFIG}
                onBlur={editors.onExplanationBlur}
              />
            </Box>

            {/* Rich-text editor for reference (persisted via updateDraftQuestionContent on Save) */}
            <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
              Reference
            </Typography>
            <Box sx={{ mb: 1 }}>
              <JoditEditor
                value={q.reference || ""}
                config={JODIT_OPTION_CONFIG}
                onBlur={editors.onReferenceBlur}
              />
            </Box>

            {/* Classification — cascading Subject -> Topic -> Sub-topic */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Subject</InputLabel>
                <Select
                  label="Subject"
                  value={q.subjectId || ""}
                  onChange={(e) => handleSubjectChange(q, e.target.value || null)}
                >
                  <MenuItem value="">
                    <em>Unset</em>
                  </MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.subjectId} value={s.subjectId}>
                      {s.subject}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }} disabled={!q.subjectId}>
                <InputLabel>Topic</InputLabel>
                <Select
                  label="Topic"
                  value={q.topicId || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === CREATE_OPTION) {
                      openCreateDialog({
                        mode: "topic",
                        parentId: q.subjectId,
                        parentLabel: subjects.find((s) => s.subjectId === q.subjectId)?.subject || "",
                        onCreated: (created) => handleTopicChange(q, created.id),
                      });
                      return;
                    }
                    handleTopicChange(q, v || null);
                  }}
                >
                  <MenuItem value="">
                    <em>Unset</em>
                  </MenuItem>
                  {rowTopics.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                  {q.subjectId && <Divider />}
                  {q.subjectId && (
                    <MenuItem value={CREATE_OPTION} sx={{ color: "primary.main", fontWeight: 600 }}>
                      + Create new topic
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }} disabled={!q.topicId}>
                <InputLabel>Sub-topic</InputLabel>
                <Select
                  label="Sub-topic"
                  value={q.subtopicId || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === CREATE_OPTION) {
                      openCreateDialog({
                        mode: "subtopic",
                        parentId: q.topicId,
                        parentLabel: rowTopics.find((t) => t.id === q.topicId)?.name || "",
                        onCreated: (created) => handleSubtopicChange(q, created.id),
                      });
                      return;
                    }
                    handleSubtopicChange(q, v || null);
                  }}
                >
                  <MenuItem value="">
                    <em>Unset</em>
                  </MenuItem>
                  {rowSubtopics.map((st) => (
                    <MenuItem key={st.id} value={st.id}>
                      {st.name}
                    </MenuItem>
                  ))}
                  {q.topicId && <Divider />}
                  {q.topicId && (
                    <MenuItem value={CREATE_OPTION} sx={{ color: "primary.main", fontWeight: 600 }}>
                      + Create new sub-topic
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
            </>
            ) : (
            <>
              {/* ===== READ-ONLY MODE: rendered HTML (mirrors QuestionViewer convention) ===== */}
              <Box
                sx={{ mb: 1, "& img": { maxWidth: "100%" } }}
                dangerouslySetInnerHTML={{ __html: q.question || "<em>(empty question)</em>" }}
              />
              <Box sx={{ mb: 1 }}>
                {(q.answers || []).map((a, ai) => (
                  <Box key={ai} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.5 }}>
                    {a.correct ? (
                      <CheckCircleIcon sx={{ fontSize: 18, color: "#22c55e", mt: "2px", flexShrink: 0 }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "#cbd5e1", mt: "2px", flexShrink: 0 }} />
                    )}
                    <Typography variant="body2" component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                      {String.fromCharCode(65 + ai)}.
                    </Typography>
                    <Box
                      sx={{ flexGrow: 1, fontWeight: a.correct ? 700 : 400, "& img": { maxWidth: "100%" } }}
                      dangerouslySetInnerHTML={{ __html: a.answer || "" }}
                    />
                  </Box>
                ))}
              </Box>
              {q.answerExplanation && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, textDecoration: "underline", display: "block" }}>
                    Explanation
                  </Typography>
                  <Box
                    sx={{ fontSize: 14, "& img": { maxWidth: "100%" } }}
                    dangerouslySetInnerHTML={{ __html: q.answerExplanation }}
                  />
                </Box>
              )}
              {q.reference && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, textDecoration: "underline", display: "block" }}>
                    Reference
                  </Typography>
                  <Box
                    sx={{ fontSize: 14, "& img": { maxWidth: "100%" } }}
                    dangerouslySetInnerHTML={{ __html: q.reference }}
                  />
                </Box>
              )}
              {(q.questionType || q.optionType) && (
                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                  {q.questionType && <Chip size="small" variant="outlined" label={q.questionType} />}
                  {q.optionType && <Chip size="small" variant="outlined" label={q.optionType} />}
                </Box>
              )}
            </>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 1 }}>
              {isEditing(q.draftQuestionId) ? (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={
                      savingId === q.draftQuestionId ? <CircularProgress size={16} /> : <SaveIcon />
                    }
                    disabled={savingId === q.draftQuestionId}
                    onClick={() => handleSave(q)}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    disabled={savingId === q.draftQuestionId}
                    onClick={() => cancelEdit(q.draftQuestionId)}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => enterEdit(q.draftQuestionId)}
                >
                  Edit
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                color="success"
                startIcon={<ThumbUpIcon />}
                onClick={() => handleReviewStatus(q, "APPROVED")}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<BlockIcon />}
                onClick={() => handleReviewStatus(q, "REJECTED")}
              >
                Reject
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="Permanently delete this question from the upload">
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteTarget(q)}
                >
                  Delete
                </Button>
              </Tooltip>
            </Box>
          </Paper>
        );
      })}

      {/* Delete-draft confirmation. */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this question?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This permanently removes the question and its options from this upload. It cannot be
            undone.
          </Typography>
          {deleteTarget && (
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                border: "1px solid #e2e8f0",
                borderRadius: 1,
                maxHeight: 120,
                overflow: "auto",
                "& img": { maxWidth: "100%" },
              }}
              dangerouslySetInnerHTML={{ __html: deleteTarget.question || "<em>(empty question)</em>" }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deletingId != null}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDelete(deleteTarget.draftQuestionId)}
            disabled={deletingId != null}
          >
            {deletingId != null ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shared inline-create dialog for new topics/subtopics. */}
      <Dialog open={!!taxonomyDialog.open} onClose={closeCreateDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {taxonomyDialog.mode === "topic" ? "New topic" : "New sub-topic"}
        </DialogTitle>
        <DialogContent>
          {taxonomyDialog.parentLabel && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {taxonomyDialog.mode === "topic" ? "Subject: " : "Topic: "}
              <strong>{taxonomyDialog.parentLabel}</strong>
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={taxonomyDialog.mode === "topic" ? "Topic name" : "Sub-topic name"}
            value={taxonomyDialogValue}
            onChange={(e) => setTaxonomyDialogValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !taxonomyDialogSubmitting) {
                e.preventDefault();
                submitCreateDialog();
              }
            }}
            inputProps={{ maxLength: taxonomyDialog.mode === "topic" ? 40 : 255 }}
            helperText={
              taxonomyDialog.mode === "topic"
                ? "Up to 40 characters. Case-insensitive — if it already exists, the existing one will be selected."
                : "Up to 255 characters. Case-insensitive — duplicates re-use the existing entry."
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateDialog} disabled={taxonomyDialogSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitCreateDialog}
            disabled={taxonomyDialogSubmitting || !taxonomyDialogValue.trim()}
          >
            {taxonomyDialogSubmitting ? <CircularProgress size={18} color="inherit" /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
